import { useEffect, useRef, useState } from 'react';
import { faPause, faPlay, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

let currentlyPlaying: HTMLAudioElement | null = null;

const KEYBOARD_SEEK_STEP_SECONDS = 5;

// Symbolic waveform: fixed bar heights, not derived from the audio content.
const WAVEFORM_BARS = [
  'h-2',
  'h-3',
  'h-1',
  'h-4',
  'h-2',
  'h-5',
  'h-3',
  'h-2',
  'h-4',
  'h-3',
  'h-1',
  'h-4',
  'h-5',
  'h-3',
  'h-2',
  'h-4',
  'h-2',
  'h-3',
  'h-5',
  'h-2',
  'h-4',
  'h-3',
  'h-1',
  'h-3',
  'h-4',
  'h-2',
  'h-5',
  'h-3',
  'h-2',
  'h-4',
  'h-3',
  'h-2',
  'h-1',
  'h-4',
  'h-3',
  'h-2',
];

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return '00:00';
  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

type Props = {
  url: string;
  onRemove?: () => void;
};

export const PostAudio = ({ url, onRemove }: Props) => {
  const ref = useRef<HTMLAudioElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const progress = duration > 0 ? currentTime / duration : 0;
  const playedBars = Math.round(progress * WAVEFORM_BARS.length);

  const toggle = () => {
    const element = ref.current;
    if (!element) return;
    if (element.paused) {
      void element.play();
    } else {
      element.pause();
    }
  };

  const seekToClientX = (clientX: number) => {
    const element = ref.current;
    const bars = barsRef.current;
    if (!element || !bars || !element.duration) return;
    const rect = bars.getBoundingClientRect();
    const fraction = Math.min(
      1,
      Math.max(0, (clientX - rect.left) / rect.width),
    );
    const time = fraction * element.duration;
    element.currentTime = time;
    setCurrentTime(time);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    seekToClientX(event.clientX);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      seekToClientX(event.clientX);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const element = ref.current;
    if (!element || !element.duration) return;
    if (event.key === 'ArrowRight') {
      element.currentTime = Math.min(
        element.duration,
        element.currentTime + KEYBOARD_SEEK_STEP_SECONDS,
      );
    } else if (event.key === 'ArrowLeft') {
      element.currentTime = Math.max(
        0,
        element.currentTime - KEYBOARD_SEEK_STEP_SECONDS,
      );
    } else {
      return;
    }
    event.preventDefault();
    setCurrentTime(element.currentTime);
  };

  const onPlay = () => {
    const element = ref.current;
    if (!element) return;
    if (currentlyPlaying && currentlyPlaying !== element) {
      currentlyPlaying.pause();
    }
    currentlyPlaying = element;
    setIsPlaying(true);
  };

  const onPause = () => {
    if (currentlyPlaying === ref.current) {
      currentlyPlaying = null;
    }
    setIsPlaying(false);
  };

  const onEnded = () => {
    onPause();
    if (ref.current) ref.current.currentTime = 0;
    setCurrentTime(0);
  };

  useEffect(() => {
    const element = ref.current;
    return () => {
      if (currentlyPlaying === element) {
        currentlyPlaying = null;
      }
    };
  }, []);

  return (
    <div className="inline-flex w-fit max-w-full items-center gap-3 rounded-full bg-gray-100 py-1 pl-1 pr-4">
      <button
        type="button"
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        onClick={toggle}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-black text-sm text-white"
      >
        <FontAwesomeIcon
          icon={isPlaying ? faPause : faPlay}
          className={isPlaying ? undefined : 'translate-x-[1px]'}
        />
      </button>
      <div
        ref={barsRef}
        role="slider"
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={Math.floor(duration)}
        aria-valuenow={Math.floor(currentTime)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onKeyDown={onKeyDown}
        className="flex touch-none cursor-pointer items-center gap-1"
      >
        {WAVEFORM_BARS.map((height, index) => (
          <span
            key={index}
            className={`w-0.5 rounded-full ${height} ${
              index < playedBars ? 'bg-gray-800' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
      <span className="shrink-0 text-sm tabular-nums text-gray-700">
        {formatTime(isPlaying || currentTime > 0 ? currentTime : duration)}
      </span>
      {onRemove && (
        <button
          type="button"
          aria-label="Remove audio"
          onClick={onRemove}
          className="flex shrink-0 items-center justify-center text-black transition-colors hover:text-gray-700"
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </button>
      )}
      <audio
        ref={ref}
        src={url}
        preload="metadata"
        onPlay={onPlay}
        onPause={onPause}
        onEnded={onEnded}
        onTimeUpdate={(event) =>
          setCurrentTime(event.currentTarget.currentTime)
        }
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        className="hidden"
      />
    </div>
  );
};
